/**
 * LevNutri - Anamnese (Ficha Clínica)
 */

import React, { useState, useEffect } from 'react';
import { anamneseService, patientsService } from '../../services/api';
import { Ico, emo } from '../ui/Ico';

const INITIAL_FORM = {
  queixa_principal:          '',
  historico_doencas:         '',
  historico_cirurgias:       '',
  medicamentos_uso:          '',
  historico_familiar:        '',
  num_refeicoes:             '',
  horarios_refeicoes:        '',
  preferencias_alimentares:  '',
  'intolerâncias_alergias':  '',
  habito_intestinal:         '',
  frequencia_intestinal:     '',
  horas_sono:                '',
  qualidade_sono:            '',
  pratica_atividade_fisica:  0,
  tipo_atividade:            '',
  frequencia_atividade:      '',
  duracao_atividade:         '',
  consumo_agua:              '',
  consumo_alcool:            '',
  observacoes:               '',
};

function Anamnese({ patientId: initialPatientId, onNavigate }) {
  const [patients, setPatients]   = useState([]);
  const [patientId, setPatientId] = useState(initialPatientId || '');
  const [patient, setPatient]     = useState(null);
  const [formData, setFormData]   = useState(INITIAL_FORM);
  const [isEditing, setIsEditing] = useState(false);
  const [hasRecord, setHasRecord] = useState(false);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    patientsService.getAll().then(res => {
      if (res.success) setPatients(res.data);
    });
  }, []);

  useEffect(() => {
    if (!patientId) return;
    const p = patients.find(p => String(p.id) === String(patientId));
    setPatient(p || null);
    loadAnamnese(patientId);
  }, [patientId, patients]);

  const loadAnamnese = async (pid) => {
    setLoading(true);
    setError('');
    try {
      const res = await anamneseService.getByPatient(pid);
      if (res.success && res.data) {
        const d = res.data;
        setFormData({
          queixa_principal:          d.queixa_principal          ?? '',
          historico_doencas:         d.historico_doencas         ?? '',
          historico_cirurgias:       d.historico_cirurgias       ?? '',
          medicamentos_uso:          d.medicamentos_uso          ?? '',
          historico_familiar:        d.historico_familiar        ?? '',
          num_refeicoes:             d.num_refeicoes             ?? '',
          horarios_refeicoes:        d.horarios_refeicoes        ?? '',
          preferencias_alimentares:  d.preferencias_alimentares  ?? '',
          'intolerâncias_alergias':  d['intolerâncias_alergias'] ?? '',
          habito_intestinal:         d.habito_intestinal         ?? '',
          frequencia_intestinal:     d.frequencia_intestinal     ?? '',
          horas_sono:                d.horas_sono                ?? '',
          qualidade_sono:            d.qualidade_sono            ?? '',
          pratica_atividade_fisica:  d.pratica_atividade_fisica  ?? 0,
          tipo_atividade:            d.tipo_atividade            ?? '',
          frequencia_atividade:      d.frequencia_atividade      ?? '',
          duracao_atividade:         d.duracao_atividade         ?? '',
          consumo_agua:              d.consumo_agua              ?? '',
          consumo_alcool:            d.consumo_alcool            ?? '',
          observacoes:               d.observacoes               ?? '',
        });
        setHasRecord(true);
        setIsEditing(false);
      } else {
        setFormData(INITIAL_FORM);
        setHasRecord(false);
        setIsEditing(true);
      }
    } catch (e) {
      setError('Erro ao carregar anamnese');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!patientId) { setError('Selecione um paciente'); return; }
    setSaving(true);
    setError('');
    setSuccessMsg('');
    try {
      const payload = { ...formData, patient_id: patientId };
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = null;
      });

      const res = await anamneseService.upsert(payload);
      if (res.success) {
        setSuccessMsg(res.message || 'Anamnese salva com sucesso!');
        setHasRecord(true);
        setIsEditing(false);
        loadAnamnese(patientId);
      } else {
        setError(res.error || 'Erro ao salvar');
      }
    } catch (e) {
      setError('Erro ao salvar anamnese');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (v) => v || <span style={{ color: '#a39b93' }}>Não informado</span>;
  const fmtBool = (v) => v ? 'Sim' : 'Não';

  return (
    <div style={styles.page}>
      {/* Cabeçalho */}
      <div style={styles.header}>
        <div>
          {onNavigate && (
            <button style={styles.backBtn}
              onClick={() => onNavigate(initialPatientId ? 'patient-detail' : 'patients', initialPatientId)}>
              ← Voltar
            </button>
          )}
          <h1 style={styles.title}><Ico e="📋" sp />Anamnese</h1>
          <p style={styles.subtitle}>Ficha Clínica Completa</p>
        </div>
        {hasRecord && !isEditing && patientId && (
          <button style={styles.primaryBtn} onClick={() => { setIsEditing(true); setSuccessMsg(''); }}>
            <Ico e="✏" sp />Editar Anamnese
          </button>
        )}
      </div>

      {/* Seletor de Paciente */}
      <div style={styles.card}>
        <label style={styles.label}>Paciente</label>
        <select style={styles.select} value={patientId}
          onChange={e => { setPatientId(e.target.value); setSuccessMsg(''); setError(''); }}>
          <option value="">Selecione um paciente...</option>
          {patients.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {error      && <div style={styles.error}>{error}</div>}
      {successMsg && <div style={styles.success}>{successMsg}</div>}

      {patientId && loading && <p style={styles.emptyMsg}>Carregando...</p>}

      {patientId && !loading && (
        isEditing ? (
          /* FORMULÁRIO */
          <form onSubmit={handleSubmit}>
            <Section title="Queixa Principal">
              <FieldFull label="Queixa principal / motivo da consulta">
                <textarea name="queixa_principal" rows={3} style={styles.textarea}
                  value={formData.queixa_principal} onChange={handleChange}
                  placeholder="Descreva o principal motivo que trouxe o paciente à consulta..." />
              </FieldFull>
            </Section>

            <Section title="Histórico Clínico">
              <FieldFull label="Histórico de doenças">
                <textarea name="historico_doencas" rows={3} style={styles.textarea}
                  value={formData.historico_doencas} onChange={handleChange}
                  placeholder="Doenças crônicas, condições de saúde existentes..." />
              </FieldFull>
              <FieldFull label="Histórico de cirurgias">
                <textarea name="historico_cirurgias" rows={2} style={styles.textarea}
                  value={formData.historico_cirurgias} onChange={handleChange}
                  placeholder="Cirurgias realizadas, datas aproximadas..." />
              </FieldFull>
              <FieldFull label="Medicamentos em uso">
                <textarea name="medicamentos_uso" rows={3} style={styles.textarea}
                  value={formData.medicamentos_uso} onChange={handleChange}
                  placeholder="Nome, dosagem e frequência dos medicamentos..." />
              </FieldFull>
            </Section>

            <Section title="Histórico Familiar">
              <FieldFull label="Doenças na família">
                <textarea name="historico_familiar" rows={3} style={styles.textarea}
                  value={formData.historico_familiar} onChange={handleChange}
                  placeholder="Diabetes, hipertensão, doenças cardíacas, obesidade, câncer..." />
              </FieldFull>
            </Section>

            <Section title="Hábitos Alimentares">
              <div style={styles.grid2}>
                <div style={styles.field}>
                  <label style={styles.label}>Nº de refeições por dia</label>
                  <input type="number" name="num_refeicoes" min="1" max="12" style={styles.input}
                    placeholder="Ex: 5" value={formData.num_refeicoes} onChange={handleChange} />
                </div>
              </div>
              <FieldFull label="Horários das refeições">
                <input type="text" name="horarios_refeicoes" style={styles.input}
                  placeholder="Ex: Café 7h, Almoço 12h, Jantar 19h..." value={formData.horarios_refeicoes} onChange={handleChange} />
              </FieldFull>
              <FieldFull label="Preferências alimentares">
                <textarea name="preferencias_alimentares" rows={2} style={styles.textarea}
                  value={formData.preferencias_alimentares} onChange={handleChange}
                  placeholder="Alimentos preferidos, culinária favorita..." />
              </FieldFull>
              <FieldFull label="Intolerâncias e alergias alimentares">
                <textarea name="intolerâncias_alergias" rows={2} style={styles.textarea}
                  value={formData['intolerâncias_alergias']} onChange={handleChange}
                  placeholder="Lactose, glúten, amendoim, frutos do mar..." />
              </FieldFull>
            </Section>

            <Section title="Hábito Intestinal">
              <div style={styles.grid2}>
                <div style={styles.field}>
                  <label style={styles.label}>Funcionamento intestinal</label>
                  <select name="habito_intestinal" style={styles.input}
                    value={formData.habito_intestinal} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    <option value="normal">Normal</option>
                    <option value="constipado">Constipado (intestino preso)</option>
                    <option value="diarreia">Diarreia frequente</option>
                    <option value="alternado">Alternado</option>
                  </select>
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Frequência intestinal</label>
                  <select name="frequencia_intestinal" style={styles.input}
                    value={formData.frequencia_intestinal} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    <option value="mais_1x_dia">Mais de 1x ao dia</option>
                    <option value="1x_dia">1x ao dia</option>
                    <option value="2-3x_semana">2–3x por semana</option>
                    <option value="1x_semana">1x por semana</option>
                    <option value="menos_1x_semana">Menos de 1x por semana</option>
                  </select>
                </div>
              </div>
            </Section>

            <Section title="Sono">
              <div style={styles.grid2}>
                <div style={styles.field}>
                  <label style={styles.label}>Horas de sono por noite</label>
                  <input type="number" name="horas_sono" step="0.5" min="0" max="24" style={styles.input}
                    placeholder="Ex: 7.5" value={formData.horas_sono} onChange={handleChange} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Qualidade do sono</label>
                  <select name="qualidade_sono" style={styles.input}
                    value={formData.qualidade_sono} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    <option value="otimo">Ótimo</option>
                    <option value="bom">Bom</option>
                    <option value="regular">Regular</option>
                    <option value="ruim">Ruim</option>
                    <option value="pessimo">Péssimo</option>
                  </select>
                </div>
              </div>
            </Section>

            <Section title="Atividade Física">
              <div style={styles.checkboxRow}>
                <input type="checkbox" name="pratica_atividade_fisica" id="pratica_af"
                  checked={!!formData.pratica_atividade_fisica} onChange={handleChange} style={{ marginRight: '0.5rem' }} />
                <label htmlFor="pratica_af" style={{ ...styles.label, margin: 0 }}>Pratica atividade física?</label>
              </div>
              {!!formData.pratica_atividade_fisica && (
                <div style={{ ...styles.grid2, marginTop: '1rem' }}>
                  <FieldFull label="Tipo de atividade">
                    <input type="text" name="tipo_atividade" style={styles.input}
                      placeholder="Ex: Musculação, corrida, natação..." value={formData.tipo_atividade} onChange={handleChange} />
                  </FieldFull>
                  <div style={styles.field}>
                    <label style={styles.label}>Frequência semanal</label>
                    <select name="frequencia_atividade" style={styles.input}
                      value={formData.frequencia_atividade} onChange={handleChange}>
                      <option value="">Selecione...</option>
                      <option value="1x">1x por semana</option>
                      <option value="2x">2x por semana</option>
                      <option value="3x">3x por semana</option>
                      <option value="4x">4x por semana</option>
                      <option value="5x">5x por semana</option>
                      <option value="diario">Diariamente</option>
                    </select>
                  </div>
                  <div style={styles.field}>
                    <label style={styles.label}>Duração por sessão (min)</label>
                    <input type="number" name="duracao_atividade" min="0" style={styles.input}
                      placeholder="Ex: 60" value={formData.duracao_atividade} onChange={handleChange} />
                  </div>
                </div>
              )}
            </Section>

            <Section title="Consumo">
              <div style={styles.grid2}>
                <div style={styles.field}>
                  <label style={styles.label}>Consumo de água (litros/dia)</label>
                  <input type="number" name="consumo_agua" step="0.1" min="0" style={styles.input}
                    placeholder="Ex: 2.0" value={formData.consumo_agua} onChange={handleChange} />
                </div>
                <div style={styles.field}>
                  <label style={styles.label}>Consumo de álcool</label>
                  <select name="consumo_alcool" style={styles.input}
                    value={formData.consumo_alcool} onChange={handleChange}>
                    <option value="">Selecione...</option>
                    <option value="nao_consome">Não consome</option>
                    <option value="raramente">Raramente (eventos sociais)</option>
                    <option value="fim_semana">Fins de semana</option>
                    <option value="2-3x_semana">2–3x por semana</option>
                    <option value="diario">Diariamente</option>
                  </select>
                </div>
              </div>
            </Section>

            <Section title="Observações Adicionais">
              <FieldFull label="">
                <textarea name="observacoes" rows={4} style={styles.textarea}
                  value={formData.observacoes} onChange={handleChange}
                  placeholder="Informações adicionais relevantes..." />
              </FieldFull>
            </Section>

            <div style={styles.formActions}>
              {hasRecord && (
                <button type="button" style={styles.secondaryBtn}
                  onClick={() => { setIsEditing(false); loadAnamnese(patientId); }}>
                  Cancelar
                </button>
              )}
              <button type="submit" style={styles.primaryBtn} disabled={saving}>
                {saving ? 'Salvando...' : (hasRecord ? 'Atualizar Anamnese' : 'Salvar Anamnese')}
              </button>
            </div>
          </form>
        ) : (
          /* VISUALIZAÇÃO */
          <div>
            <ViewSection title="Queixa Principal">
              <ViewField label="Queixa principal" value={fmt(formData.queixa_principal)} />
            </ViewSection>

            <ViewSection title="Histórico Clínico">
              <ViewField label="Doenças" value={fmt(formData.historico_doencas)} />
              <ViewField label="Cirurgias" value={fmt(formData.historico_cirurgias)} />
              <ViewField label="Medicamentos" value={fmt(formData.medicamentos_uso)} />
            </ViewSection>

            <ViewSection title="Histórico Familiar">
              <ViewField label="Doenças na família" value={fmt(formData.historico_familiar)} />
            </ViewSection>

            <ViewSection title="Hábitos Alimentares">
              <ViewField label="Refeições/dia" value={fmt(formData.num_refeicoes)} />
              <ViewField label="Horários" value={fmt(formData.horarios_refeicoes)} />
              <ViewField label="Preferências" value={fmt(formData.preferencias_alimentares)} />
              <ViewField label="Intolerâncias/Alergias" value={fmt(formData['intolerâncias_alergias'])} />
            </ViewSection>

            <ViewSection title="Hábito Intestinal">
              <ViewField label="Funcionamento" value={fmt(formData.habito_intestinal)} />
              <ViewField label="Frequência" value={fmt(formData.frequencia_intestinal)} />
            </ViewSection>

            <ViewSection title="Sono">
              <ViewField label="Horas de sono" value={formData.horas_sono ? `${formData.horas_sono}h` : fmt(null)} />
              <ViewField label="Qualidade" value={fmt(formData.qualidade_sono)} />
            </ViewSection>

            <ViewSection title="Atividade Física">
              <ViewField label="Pratica atividade física" value={fmtBool(formData.pratica_atividade_fisica)} />
              {!!formData.pratica_atividade_fisica && <>
                <ViewField label="Tipo" value={fmt(formData.tipo_atividade)} />
                <ViewField label="Frequência" value={fmt(formData.frequencia_atividade)} />
                <ViewField label="Duração" value={formData.duracao_atividade ? `${formData.duracao_atividade} min` : fmt(null)} />
              </>}
            </ViewSection>

            <ViewSection title="Consumo">
              <ViewField label="Água/dia" value={formData.consumo_agua ? `${formData.consumo_agua} L` : fmt(null)} />
              <ViewField label="Álcool" value={fmt(formData.consumo_alcool)} />
            </ViewSection>

            {formData.observacoes && (
              <ViewSection title="Observações">
                <ViewField label="" value={fmt(formData.observacoes)} />
              </ViewSection>
            )}
          </div>
        )
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={styles.card}>
      <h2 style={styles.sectionTitle}>{emo(title)}</h2>
      {children}
    </div>
  );
}

function FieldFull({ label, children }) {
  return (
    <div style={{ ...styles.field, marginBottom: '1rem' }}>
      {emo(label && <label style={styles.label}>{emo(label)}</label>)}
      {children}
    </div>
  );
}

function ViewSection({ title, children }) {
  return (
    <div style={styles.card}>
      <h2 style={styles.sectionTitle}>{emo(title)}</h2>
      <div style={styles.viewGrid}>{children}</div>
    </div>
  );
}

function ViewField({ label, value }) {
  return (
    <div style={styles.viewField}>
      {emo(label && <span style={styles.viewLabel}>{emo(label)}</span>)}
      <span style={styles.viewValue}>{value}</span>
    </div>
  );
}

const styles = {
  page: { padding: '2rem', maxWidth: '900px', margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem' },
  title: { fontSize: '1.75rem', fontWeight: 600, color: '#2f2a26', margin: '0 0 0.25rem' },
  subtitle: { color: '#736b64', fontSize: '0.9rem', margin: 0 },
  backBtn: { background: 'none', border: 'none', color: '#D4A574', cursor: 'pointer', fontSize: '0.9rem', padding: '0 0 0.5rem', display: 'block' },
  card: { background: '#ffffff', borderRadius: '12px', padding: '1.5rem', marginBottom: '1.25rem', boxShadow: '0 2px 8px rgba(44,44,44,0.07)' },
  sectionTitle: { fontSize: '1rem', fontWeight: 600, color: '#2f2a26', marginTop: 0, marginBottom: '1rem', paddingBottom: '0.5rem', borderBottom: '1px solid #ebe3da' },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#4a4440', marginBottom: '0.4rem' },
  select: { width: '100%', maxWidth: '400px', padding: '0.65rem 1rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.95rem', color: '#2f2a26', background: '#ffffff', outline: 'none' },
  input: { width: '100%', padding: '0.65rem 1rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.95rem', color: '#2f2a26', boxSizing: 'border-box', outline: 'none' },
  textarea: { width: '100%', padding: '0.65rem 1rem', border: '1.5px solid #d9d0c6', borderRadius: '8px', fontSize: '0.95rem', color: '#2f2a26', resize: 'vertical', boxSizing: 'border-box', outline: 'none' },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px,1fr))', gap: '1rem', marginBottom: '0.5rem' },
  field: { display: 'flex', flexDirection: 'column' },
  checkboxRow: { display: 'flex', alignItems: 'center', marginBottom: '0.5rem' },
  formActions: { display: 'flex', gap: '1rem', justifyContent: 'flex-end', marginTop: '0.5rem', marginBottom: '2rem' },
  primaryBtn: { background: 'linear-gradient(135deg,#D4A574,#e8c9a8)', color: '#ffffff', border: 'none', borderRadius: '8px', padding: '0.7rem 1.75rem', fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer' },
  secondaryBtn: { background: '#f6f0ea', color: '#4a4440', border: 'none', borderRadius: '8px', padding: '0.7rem 1.5rem', fontSize: '0.95rem', cursor: 'pointer' },
  viewGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px,1fr))', gap: '0.75rem' },
  viewField: { display: 'flex', flexDirection: 'column', gap: '2px' },
  viewLabel: { fontSize: '0.78rem', fontWeight: 600, color: '#736b64', textTransform: 'uppercase', letterSpacing: '0.05em' },
  viewValue: { fontSize: '0.95rem', color: '#2f2a26' },
  emptyMsg: { color: '#736b64', textAlign: 'center', padding: '2rem 0' },
  error:   { background: '#f8ebe9', color: '#a8483f', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' },
  success: { background: '#edf4ee', color: '#46714f', padding: '0.75rem 1rem', borderRadius: '8px', marginBottom: '1rem', fontSize: '0.9rem' },
};

export default Anamnese;
